<template>
  <div class="plots" id="million-bit-plots">
    
    <!-- Rewards Section -->
    <div v-if="canClaimRewards && appData().connectedAccount.address" class="rewards-section mb-5">
      <h2 class="mb-3 honk-text">Rewards</h2>
      <div class="rewards-card">
        <div v-if="rewardsLoading" class="loading-container rewards-loading">
          <div class="loading-spinner light"></div>
          <p class="loading-text light">Checking rewards...</p>
        </div>
        <div v-else>
          <div class="rewards-amount">
            <h3 class="mb-2">Available to Claim</h3>
            <div class="eth-amount">
              <span class="amount">{{ formattedRewards }}</span>
              <span class="currency">ETH</span>
            </div>
            <!-- <p class="text-muted small" v-if="claimableTokenIds.length > 0">
              From {{ claimableTokenIds.length }} plot{{ claimableTokenIds.length !== 1 ? 's' : '' }}
            </p> -->
          </div>
          <button
            v-if="hasClaimableRewards"
            @click="claimRewards"
            class="btn btn-success btn-lg btn-block claim-button"
            :disabled="isClaimingRewards"
          >
            <span v-if="!isClaimingRewards">💰 Claim Rewards</span>
            <span v-else>
              <div class="loader-inline"></div>
              Claiming...
            </span>
          </button>
          <p v-else class="text-center text-muted mt-3">
            No rewards available to claim yet
          </p>
        </div>
      </div>
    </div>

    <h2 class="mb-4 honk-text">My Plots</h2>
    <p v-if="!appData().connectedAccount.address">Connect your wallet to view your plots</p>

    <div v-if="nfts.length > 0">
        <div v-for="(nft, index) in nfts" :key="nft.tokenId" class="nft-card">
            <!-- <p>Token ID: {{ nft.tokenId }}</p> -->
            <img :src="nft.image" alt="NFT Image" class="plot-nft" :ref="'cardImage-'+index" />
            <canvas 
                :ref="'canvas-' + index" 
                :width="nft.width" 
                :height="nft.height"
            ></canvas>
            <p>
                <!-- Hidden File Input to pick an image -->
                <input 
                type="file" 
                ref="fileInputs"
                :ref="'fileInput-' + index" 
                @change="onFileChange($event, index, nft)" 
                accept="image/*" 
                style="display: none;" 
                />

                <!-- URL text input -->
                <div v-if="nft.showForm" class="">
                    <input type="text" v-model="nft.link" placeholder="Enter the URL of the new image" class="form-control form-control-md mb-2" />
                    <button v-on:click="submitNewImage(index, nft)" class="btn btn-warning btn-block mb-4" v-bind:disabled="nft.isLoading">
                        <span v-if="!nft.isLoading">SAVE for <span v-text="repaintPriceEth"></span> eth</span>
                        <span v-else class="saving-state"><span class="loading-spinner-small"></span> Saving...</span>
                    </button>
                </div>
                <div class="btn-group btn-block">
                    <a v-bind:href="'https://magiceden.us/item-details/base/'+contractAddress+'/'+nft.tokenId" target="_blank" class="btn btn-outline-primary">Marketplace</a>
                    <a @click.prevent="repaint(index, nft)" href="#" target="_blank" class="btn btn-outline-primary">Repaint</a>
                </div>
                <ul class="p-0">
                    <li v-for="(value, key) in nft.attributes" :key="key" v-if="value.trait_type !== 'X1' && value.trait_type !== 'Y1' && value.trait_type !== 'X2' && value.trait_type !== 'Y2'">
                        <strong>{{ value.trait_type }}:</strong> {{ value.value }}
                    </li>
                </ul>
            </p>
            <hr />
        </div>
    </div>
    <div v-else>
        <div>
            <p v-if="!appData().connectedAccount.address">Connect your wallet to see your plot NFTs here. Then you can repaint your pixels 👩‍🎨</p>
            <div v-else>
                <div v-if="nftsLoading" class="loading-container">
                    <div class="loading-spinner"></div>
                    <p class="loading-text">Loading your plots...</p>
                </div>
                <div v-else>
                    <p v-if="nftsLoadError" class="text-danger">Failed to load plots. Please try again.</p>
                    <p v-else>No plots found.</p>
                    <button class="btn btn-primary" v-on:click="getNFTs" :disabled="nftsLoading">
                        <span v-if="!nftsLoading">
                            <span v-if="nftsLoadError">🔄 Retry</span>
                            <span v-else>🔄 Refresh</span>
                        </span>
                        <span v-else>Loading...</span>
                    </button>
                </div>
            </div>
        </div>
    </div>

    <b-modal id="modal-repaint-success" title="You're a part of onchain history!" hide-footer size="lg">
      <p>
        Your repainted plot has been submitted. <a v-if="trxHashSuccess" v-bind:href="'https://basescan.org/tx/'+trxHashSuccess" target="_blank">View transaction</a>
        <br />
        Please allow a few minutes for the transaction to be confirmed.
      </p>
    </b-modal>
  </div>
</template>

<script>
import utils from '../utils';
import config from '../config';

export default {
  name: 'plots',
  data () {
    return {
        contractAddress: config.NFT_CA,
        rewardsContractAddress: config.NFT_REWARDS_CA,
        nftsLoading: false,
        nfts: [],
        nftsLoadError: false,
        repaintPrice: undefined,
        trxHashSuccess: undefined,
        totalClaimableRewards: '0',
        claimableTokenIds: [],
        rewardsLoading: false,
        isClaimingRewards: false,
        canClaimRewards: config.SHOW_REWARDS,
    }
  },
  computed: {
    formattedRewards() {
      if (!this.totalClaimableRewards || this.totalClaimableRewards === '0' || this.totalClaimableRewards === 0) {
        return '0.000000';
      }
      
      try {
        const formatted = ethers.formatEther(this.totalClaimableRewards);
        return parseFloat(formatted).toFixed(6);
      } catch (e) {
        console.error('Error formatting rewards:', e);
        return '0.000000';
      }
    },
    totalClaimableEth() {
      return this.formattedRewards;
    },
    hasClaimableRewards() {
      const rewards = this.totalClaimableRewards;
      const hasValue = rewards && rewards !== '0' && rewards !== 0 && parseFloat(rewards) > 0;
      const hasTokens = this.claimableTokenIds.length > 0;
      return hasValue && hasTokens;
    }
  },
  methods: {
    navigateTo: function(route) {
      this.$router.push(route);
    },
    repaint: function(index, nft) {
        console.log('Repainting NFT', nft);
        this.$refs[`fileInput-${index}`][0].click();
    },
    onFileChange(event, index, nft) {
        nft.showForm = true;
        console.log('File changed', index, nft);
        // return (event) => {
            console.log('File changed event', event);
            // Get the selected file
            const file = event.target.files[0];
            console.log('File', file);
            if (file && file.type.startsWith('image/')) {
                // Create a FileReader to read the image
                const reader = new FileReader();
                reader.onload = (e) => {
                    console.log('File reader loaded', e);
                    // Create a new image element
                    const img = new Image();
                    img.onload = () => {
                        // Get the canvas context and draw the image
                        const canvas = this.$refs[`canvas-${index}`][0];
                        const ctx = canvas.getContext('2d');
                        
                        // Clear the canvas before drawing a new image
                        ctx.clearRect(0, 0, nft.width, nft.height);

                        ctx.imageSmoothingEnabled = true;
                        ctx.imageSmoothingQuality = 'high';

                        // Draw the image onto the canvas
                        ctx.drawImage(img, 0, 0, nft.width, nft.height);

                        const imageData = ctx.getImageData(0, 0, nft.width, nft.height);
                        utils.replaceTransparent(imageData.data);
                        ctx.putImageData(imageData, 0, 0);

                        // Place the canvas over the card image
                        const cardImage = this.$refs[`cardImage-${index}`][0];
                        const cardImageWidth = cardImage.width;
                        const imgRatio = cardImageWidth / 128;
                        canvas.style.width = nft.width * imgRatio + 'px';
                        canvas.style.height = nft.height * imgRatio + 'px';
                        canvas.style.top = (((128 / 2) + 16 - (nft.height / 2)) * imgRatio) + 'px'; // (((144 * imgRatio)) / 2) + (17 * imgRatio) - ((nft.height * imgRatio)) + 'px';
                        canvas.style.left = (((128 * imgRatio)) / 2) - ((nft.width * imgRatio) / 2) + 'px';
                    };
                    // Set the source of the image element to the file data
                    img.src = e.target.result;
                };
                // Read the image file as a data URL
                reader.readAsDataURL(file);
            }
        // };
    },
    async getRepaintPrice() {
        console.log('Getting repaint price');
        const { provider } = await this.getProviderSigner();
        const contract = new ethers.Contract(this.contractAddress, config.NFT_ABI, provider);
        const price = await contract.repaintPrice();
        console.log('Repaint price', price);
        this.repaintPrice = price;
        this.repaintPriceEth = ethers.formatEther(price);
        return price;
    },
    async getClaimableRewards() {
        if (!this.appData().connectedAccount.address) {
            console.log('No connected account, skipping rewards check');
            return;
        }
        
        if (this.nfts.length === 0) {
            console.log('No NFTs loaded yet, skipping rewards check');
            return;
        }
        
        console.log('Checking rewards for', this.nfts.length, 'NFTs');
        this.rewardsLoading = true;
        
        try {
            const { provider } = await this.getProviderSigner();
            const rewardsContract = new ethers.Contract(
                this.rewardsContractAddress,
                config.NFT_REWARDS_ABI,
                provider
            );

            let totalRewards = BigInt(0);
            const claimableIds = [];

            // Check rewards for each NFT
            for (const nft of this.nfts) {
                try {
                    console.log(`Checking rewards for token ${nft.tokenId}`);
                    const allocation = await rewardsContract.getTokenIdAllocation(nft.tokenId);
                    console.log(`Token ${nft.tokenId} allocation:`, allocation.toString());
                    
                    if (allocation > 0) {
                        totalRewards = totalRewards + allocation;
                        claimableIds.push(nft.tokenId);
                        console.log(`Token ${nft.tokenId} has rewards:`, ethers.formatEther(allocation), 'ETH');
                    }
                } catch (e) {
                    console.error(`Error checking rewards for token ${nft.tokenId}:`, e);
                }
            }

            console.log('Total rewards:', ethers.formatEther(totalRewards), 'ETH');
            console.log('Claimable token IDs:', claimableIds);
            
            this.totalClaimableRewards = totalRewards.toString();
            this.claimableTokenIds = claimableIds;
            this.rewardsLoading = false;
        } catch (e) {
            console.error('Error getting claimable rewards:', e);
            this.totalClaimableRewards = '0';
            this.claimableTokenIds = [];
            this.rewardsLoading = false;
        }
    },
    async claimRewards() {
        if (this.claimableTokenIds.length === 0) return;
        
        this.isClaimingRewards = true;
        try {
            const { signer } = await this.getProviderSigner();
            const userAddress = this.appData().connectedAccount.address;
            const rewardsContract = new ethers.Contract(
                this.rewardsContractAddress,
                config.NFT_REWARDS_ABI,
                signer
            );

            console.log('Claiming rewards for tokens:', this.claimableTokenIds);
            
            const tx = await rewardsContract.claimAllocation(userAddress, this.claimableTokenIds);
            console.log('Claim transaction:', tx);
            
            const receipt = await tx.wait();
            console.log('Claim receipt:', receipt);
            
            // Reset rewards after successful claim
            this.totalClaimableRewards = '0';
            this.claimableTokenIds = [];
            
            // Show success message
            this.showAlert(`Successfully claimed!`);
            utils.confetti(5000);
            
            this.isClaimingRewards = false;
        } catch (e) {
            this.isClaimingRewards = false;
            console.error('Error claiming rewards:', e);
            this.handleWalletError(e);
        }
    },
    // Get the NFTs owned by the connected wallet
    async getNFTs() {
        if (!this.appData().connectedAccount.address) {
            console.log('No connected account, skipping getNFTs');
            this.nftsLoading = false;
            return;
        }
        
        this.nftsLoading = true;
        this.nftsLoadError = false;
        await this.getRepaintPrice();
        try {
        const userAddress = this.appData().connectedAccount.address;
        let { provider, signer } = await this.getProviderSigner();
      const contract = new ethers.Contract(this.contractAddress, config.NFT_ABI, signer);

      // Get the user's balance of NFTs
      const balance = await contract.balanceOf(userAddress);
      const nftData = [];

      // Loop through each token the user owns
      for (let i = 0; i < balance; i++) {
        const tokenId = await contract.tokenOfOwnerByIndex(userAddress, i);

        let nft = {
            showForm: false,
            canBuy: false,
            tokenId: tokenId.toString(),
            isLoading: false,
        }

        // Fetch the metadata for each token (assuming it uses tokenURI)
        const tokenURI = await contract.tokenURI(tokenId);

        try {
            // Fetch the metadata from the tokenURI (typically JSON hosted on IPFS or other storage)
            const metadata = await fetch(tokenURI).then((response) => response.json());

            const nftLink = metadata.attributes.find((attr) => attr.trait_type === 'Link').value;
            const nftCoordinates = metadata.attributes.find((attr) => attr.trait_type === 'Coordinates').value;
            const nftCoords = nftCoordinates.split(' to ');
            const nftXY1 = nftCoords[0].substring(1, nftCoords[0].length-1).split(',');
            const nftXY2 = nftCoords[1].substring(1, nftCoords[1].length-1).split(',');
            const nftWidth = parseInt(nftXY2[0]) - parseInt(nftXY1[0]);
            const nftHeight = parseInt(nftXY2[1]) - parseInt(nftXY1[1]);
            
            nftData.push({
                ...nft,
                width: nftWidth,
                height: nftHeight,
                x1: parseInt(nftXY1[0]),
                y1: parseInt(nftXY1[1]),
                link: nftLink,
                ...metadata,
            });
        } catch(e) {
            console.log(`Error fetching metadata for token ${tokenId}`, e);
            nftData.push(nft);
            this.nftsLoading = false;
        }
      }

      // Set the NFTs in the component's state
      this.nfts = nftData;
      this.nftsLoading = false;
      
      // Get claimable rewards after loading NFTs (only if feature is enabled)
      if (this.canClaimRewards) {
        await this.getClaimableRewards();
      }
        } catch(e) {
            console.error('Error getting NFTs', e);
            this.nftsLoadError = true;
            this.nftsLoading = false;
        }
    },
    async submitNewImage(index, nft) {
        console.log('Submitting new image');
        await this.getRepaintPrice();
        const { provider, signer } = await this.getProviderSigner();
        const contract = new ethers.Contract(this.contractAddress, config.NFT_ABI, signer);

        const canvas = this.$refs[`canvas-${index}`][0];
        const ctx = canvas.getContext('2d');

        const imageData = ctx.getImageData(0, 0, nft.width, nft.height).data;
        const pixels_data = utils.getPixelsData(imageData, nft.width, nft.height);

        let pixels_data_strs = [];
        _.times(nft.height/16, function(h){
            _.times(nft.width/16, function(w){
                pixels_data_strs.push(utils.stringDataForEth(pixels_data[h][w], nft.link, 0, h, w, nft.x1, nft.y1));
            });
        });
        
        console.log('pixels_data_strs', pixels_data_strs)
        var pixels_data_str = pixels_data_strs.join('\n');
        var pixels_data_gzip_str = pako.deflate(pixels_data_str, { to: 'string' });
        
        console.log('pixels_data_str length: '+pixels_data_str.length);
        console.log('pixels_data_gzip_str length: '+pixels_data_gzip_str.length);
        console.log('compression ratio: '+(pixels_data_str.length/pixels_data_gzip_str.length));

        // console.log('New image', pixels_data_gzip_str);
        try {
            nft.isLoading = true;
            let tx;
            if (false) {
                const tokenContract = new ethers.Contract(config.TOKEN_CA, config.TOKEN_ABI, signer);
                // TODO get live value from contract
                const repaintTokenPrice = await contract.tokenRepaintPrice();
                // console.log(`Repaint token price: ${tokens}`);
                // const repaintTokenPrice = ethers.parseUnits(tokens.toString(), 18);
                console.log('Repaint token price', repaintTokenPrice);
                const approveTrx = await tokenContract.approve(config.NFT_CA, repaintTokenPrice);
                console.log(`Approved ${repaintTokenPrice} tokens for NFT contract:`, approveTrx);
                const approveReceipt = await approveTrx.wait();
                console.log('Approval receipt:', approveReceipt);
                tx = await contract.tokenRepaintPixels(nft.tokenId, pixels_data_gzip_str);
            } else {
                const estimatedGas = await contract.repaintPixels.estimateGas(nft.tokenId, pixels_data_gzip_str, {value: this.repaintPrice});
                const gasLimit = this.getGasLimitFromEstimate(estimatedGas);
        
                const overrides = {
                    gasLimit: gasLimit,
                    value: this.repaintPrice,
                };
                tx = await contract.repaintPixels(nft.tokenId, pixels_data_gzip_str, overrides);
            }
            
            console.log('Repaint transaction', tx);
            
            const receipt = await tx.wait();
            const hash = receipt.hash;
            nft.isLoading = false;

            // Show modal with success message
            this.trxHashSuccess = hash;
            this.$bvModal.show('modal-repaint-success');
            utils.confetti(10000);

            // Trigger lambda
            try {
                fetch(config.WEBHOOK_URL+'?trigger=true', {
                    method: 'GET',
                });
            } catch(e) {
            console.log('Error triggering lambda', e);
            }
        } catch(e) {
            nft.isLoading = false;

            utils.trackEvent('trxFailed');
            console.error('Error sending:', e);
            this.handleWalletError(e);
        }
    }
  },
  mounted: function () {
    if (this.appData().connectedAccount) {
        this.getNFTs();
    } else {
        this.$on('accountConnected', this.getNFTs);
    }
  },
  inject: [
    'updateAppRoute', 'appData', 'postToLambda', 'getProviderSigner',
    'handleWalletError', 'showAlert',
    'getGasLimitFromEstimate',
  ],
  created: function () {
    this.updateAppRoute('plots');
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    #million-bit-plots {
      background: #fff !important;
      padding: 20px;
    }
    .plots ul {
        list-style: none;
    }
    .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
    }
    .loading-spinner {
        width: 50px;
        height: 50px;
        border: 4px solid #e0e0e0;
        border-top: 4px solid #0052ff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }
    .loading-text {
        margin-top: 16px;
        color: #666;
        font-size: 1.1rem;
    }
    .loading-spinner.light {
        border-color: rgba(255,255,255,0.3);
        border-top-color: #fff;
    }
    .loading-text.light {
        color: #fff;
    }
    .rewards-loading {
        padding: 30px 20px;
    }
    .loader-inline {
        border: 3px solid #f3f3f3;
        border-top: 3px solid #28a745;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        animation: spin 1s linear infinite;
        display: inline-block;
        margin-right: 8px;
        vertical-align: middle;
    }
    .loading-spinner-small {
        width: 18px;
        height: 18px;
        border: 2px solid rgba(0,0,0,0.2);
        border-top: 2px solid #333;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        display: inline-block;
        vertical-align: middle;
        margin-right: 8px;
    }
    .saving-state {
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
    .rewards-section {
        max-width: 600px;
        margin: 0 auto 40px;
    }
    .rewards-card {
        background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
        border-radius: 20px;
        padding: 30px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        color: white;
    }
    .rewards-amount {
        text-align: center;
        margin-bottom: 20px;
    }
    .rewards-amount h3 {
        color: #fff;
        font-size: 1.2rem;
        font-weight: 600;
        margin-bottom: 15px;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
    .rewards-amount .text-muted {
        color: rgba(255, 255, 255, 0.95) !important;
        font-weight: 500;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    }
    .eth-amount {
        font-size: 3.5rem;
        font-weight: bold;
        line-height: 1;
        margin: 10px 0;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    }
    .eth-amount .amount {
        color: #fff;
    }
    .eth-amount .currency {
        font-size: 1.8rem;
        color: #fff;
        margin-left: 10px;
    }
    .claim-button {
        font-size: 1.3rem;
        padding: 15px 30px;
        border-radius: 12px;
        font-weight: bold;
        border: none;
        transition: all 0.3s ease;
        background: #fff;
        color: #ff6b35;
    }
    .claim-button:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
        background: #ffe6d9;
    }
    .claim-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    .nft-card {
        width: 50%;
        max-width: 500px;
        margin: 0 auto;
        position: relative;
    }
    .nft-card canvas {
        position: absolute;
    }
    img.plot-nft {
        width: 100%;
    }
    /* Mobile */
    @media screen and (max-device-width: 768px) {
        h2 {
            font-size: 4rem;
        }
        .nft-card {
            width: 80%;
            max-width: 600px;
        }
        .eth-amount {
            font-size: 2.5rem;
        }
        .eth-amount .currency {
            font-size: 1.3rem;
        }
        .rewards-card {
            padding: 20px;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/Plots.vue?b433545c